import { resolveMediaUrl } from '../../utils/helpers';
import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { X, Check, UserX, Clock, AlertCircle, Save, Calendar } from 'lucide-react';

const AttendanceMarkingModal = ({ isOpen, onClose, session, students, onSaveAttendance }) => {
  const { theme } = useTheme();

  // Initialize attendance state for all students
  const [attendance, setAttendance] = useState(
    students.reduce((acc, student) => {
      acc[student.userId] = {
        status: 'unmarked', // present, absent, late, excused, unmarked
        note: ''
      };
      return acc;
    }, {})
  );

  const [showNoteFor, setShowNoteFor] = useState(null);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleStatusChange = (userId, status) => {
    setAttendance(prev => ({
      ...prev,
      [userId]: { ...prev[userId], status }
    }));
  };

  const handleNoteChange = (userId, note) => {
    setAttendance(prev => ({
      ...prev,
      [userId]: { ...prev[userId], note }
    }));
  };

  const handleSave = () => {
    onSaveAttendance(session.id, attendance);
    onClose();
  };

  const getStatusCounts = () => {
    const counts = {
      present: 0,
      absent: 0,
      late: 0,
      excused: 0,
      unmarked: 0
    };
    Object.values(attendance).forEach(record => {
      counts[record.status]++;
    });
    return counts;
  };

  const statusCounts = getStatusCounts();
  const totalMarked = statusCounts.present + statusCounts.absent + statusCounts.late + statusCounts.excused;

  const getStatusColor = (status) => {
    switch (status) {
      case 'present': return 'bg-green-500 hover:bg-green-600 text-white';
      case 'absent': return 'bg-red-500 hover:bg-red-600 text-white';
      case 'late': return 'bg-yellow-500 hover:bg-yellow-600 text-white';
      case 'excused': return 'bg-blue-500 hover:bg-blue-600 text-white';
      default: return 'bg-gray-200 hover:bg-gray-300 text-gray-700';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'present': return <Check size={16} />;
      case 'absent': return <UserX size={16} />;
      case 'late': return <Clock size={16} />;
      case 'excused': return <AlertCircle size={16} />;
      default: return null;
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className={`${theme.card} rounded-xl w-full max-w-4xl max-h-[90vh] flex flex-col my-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex justify-between items-start">
          <div className="flex-1">
            <h2 className={`text-2xl font-bold ${theme.text} mb-2`}>Mark Attendance</h2>
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Calendar size={16} className={theme.textSecondary} />
                <span className={theme.textSecondary}>{formatDate(session.date)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock size={16} className={theme.textSecondary} />
                <span className={theme.textSecondary}>{session.time} ({session.duration} min)</span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between text-sm mb-2">
            <span className={theme.textSecondary}>Attendance Marked</span>
            <span className="font-semibold">{totalMarked}/{students.length}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all"
              style={{ width: `${(totalMarked / students.length) * 100}%` }}
            />
          </div>
          <div className="flex gap-4 text-xs">
            <span className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-green-500" />
              {statusCounts.present} Present
            </span>
            <span className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              {statusCounts.absent} Absent
            </span>
            <span className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-yellow-500" />
              {statusCounts.late} Late
            </span>
            <span className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-blue-500" />
              {statusCounts.excused} Excused
            </span>
          </div>
        </div>

        {/* Student List */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-3">
            {students.map(student => (
              <div key={student.userId} className={`border ${theme.border} rounded-lg p-4`}>
                <div className="flex items-center gap-4">
                  {/* Avatar */}
                  <img
                    src={resolveMediaUrl(student.profileImage)}
                    alt={`${student.firstName} ${student.lastName}`}
                    className="w-12 h-12 rounded-full object-cover"
                  />

                  {/* Name */}
                  <div className="flex-1">
                    <h4 className={`font-semibold ${theme.text}`}>
                      {student.firstName} {student.lastName}
                    </h4>
                    <p className={`text-sm ${theme.textSecondary}`}>
                      Attendance: {student.attendanceRate}%
                    </p>
                  </div>

                  {/* Status Buttons */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleStatusChange(student.userId, 'present')}
                      className={`px-3 py-2 rounded-lg transition-colors text-sm flex items-center gap-1 ${
                        attendance[student.userId]?.status === 'present'
                          ? getStatusColor('present')
                          : 'border border-gray-300 hover:bg-gray-50'
                      }`}
                      title="Present"
                    >
                      <Check size={16} />
                      <span className="hidden sm:inline">Present</span>
                    </button>
                    <button
                      onClick={() => handleStatusChange(student.userId, 'absent')}
                      className={`px-3 py-2 rounded-lg transition-colors text-sm flex items-center gap-1 ${
                        attendance[student.userId]?.status === 'absent'
                          ? getStatusColor('absent')
                          : 'border border-gray-300 hover:bg-gray-50'
                      }`}
                      title="Absent"
                    >
                      <UserX size={16} />
                      <span className="hidden sm:inline">Absent</span>
                    </button>
                    <button
                      onClick={() => handleStatusChange(student.userId, 'late')}
                      className={`px-3 py-2 rounded-lg transition-colors text-sm flex items-center gap-1 ${
                        attendance[student.userId]?.status === 'late'
                          ? getStatusColor('late')
                          : 'border border-gray-300 hover:bg-gray-50'
                      }`}
                      title="Late"
                    >
                      <Clock size={16} />
                      <span className="hidden sm:inline">Late</span>
                    </button>
                    <button
                      onClick={() => handleStatusChange(student.userId, 'excused')}
                      className={`px-3 py-2 rounded-lg transition-colors text-sm flex items-center gap-1 ${
                        attendance[student.userId]?.status === 'excused'
                          ? getStatusColor('excused')
                          : 'border border-gray-300 hover:bg-gray-50'
                      }`}
                      title="Excused"
                    >
                      <AlertCircle size={16} />
                      <span className="hidden sm:inline">Excused</span>
                    </button>
                  </div>

                  {/* Note Toggle */}
                  <button
                    onClick={() => setShowNoteFor(showNoteFor === student.userId ? null : student.userId)}
                    className="text-sm text-blue-600 hover:underline"
                  >
                    {showNoteFor === student.userId ? 'Hide Note' : 'Add Note'}
                  </button>
                </div>

                {/* Note Input */}
                {showNoteFor === student.userId && (
                  <div className="mt-3 pl-16">
                    <textarea
                      value={attendance[student.userId]?.note || ''}
                      onChange={(e) => handleNoteChange(student.userId, e.target.value)}
                      placeholder="Add a note (e.g., arrived 10 mins late, felt sick, etc.)"
                      className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 text-sm`}
                      rows={2}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-between items-center">
          <div className="text-sm">
            <span className={theme.textSecondary}>
              {totalMarked === students.length ? (
                <span className="text-green-600 font-semibold">✓ All students marked</span>
              ) : (
                <span>{students.length - totalMarked} student{students.length - totalMarked !== 1 ? 's' : ''} unmarked</span>
              )}
            </span>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className={`px-4 py-2 border ${theme.border} rounded-lg hover:bg-gray-50 transition-colors`}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={totalMarked === 0}
              className={`px-4 py-2 ${theme.buttonPrimary} rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <Save size={18} />
              Save Attendance
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceMarkingModal;
